import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { getSession, login, logout, type SessionResponse } from '@/api/endpoints/auth';
import { keys } from '@/api/keys';
import { outbox } from '@/offline/outbox';

/**
 * The app-boot session probe (`050-auth.md` → GET /api/auth/me).
 *
 * `null` means "signed out" — a legitimate answer, not an error, which is why
 * `getSession` maps a 401 to `null` rather than throwing. Anything else that throws
 * really is a failure (the backend is down) and gets an error state, not a redirect.
 *
 * staleTime/gcTime Infinity (`111` §3.2): it changes only on login and logout, and
 * both of those write the cache explicitly.
 */
export function useSession(): UseQueryResult<SessionResponse | null> {
  return useQuery({
    queryKey: keys.session(),
    queryFn: getSession,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,

    // `refetchOnMount: false` is load-bearing, and the reason is not obvious.
    //
    // This query has TWO observers — `RequireAuth` and `TopBar` — and `TopBar` lives INSIDE
    // the tree that `RequireAuth` only renders once the session has resolved. With the
    // default (`true`), an ERRORED session query refetches whenever a new observer mounts,
    // and a refetch with no data puts the status back to `pending`. So: the backend is down
    // → the query errors → `RequireAuth` boots the shell offline → `TopBar` mounts → a
    // second observer appears → refetch → status `pending` → `RequireAuth` swaps back to the
    // boot skeleton → `TopBar` unmounts → the query settles to `error` → the shell renders
    // again → … a render loop that fires hundreds of `GET /api/auth/me` per second and
    // leaves the user staring at a skeleton over the top of their own unsaved draft.
    //
    // The session legitimately changes only on login and logout, and both write this cache
    // explicitly. Nothing is gained by refetching it on mount, and the offline boot
    // (`stores/lastSession.ts`) depends on the error state being STABLE.
    refetchOnMount: false,

    // And `retryOnMount: false` is the one that actually closes the loop. `refetchOnMount`
    // governs a query that HAS data; a query that errored with NO data is retried when a new
    // observer subscribes unless this is set. Since a fetch that starts with no data resets
    // `status` to `pending` (that is v5's reducer, not a quirk of ours), the retry is what
    // swaps the shell back to the skeleton and starts the cycle over.
    retryOnMount: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      login(username, password),

    onSuccess: async (session) => {
      queryClient.setQueryData(keys.session(), session);

      // 050-auth.md §"What happens on a 401": the cache is not wiped on the way out,
      // but it IS invalidated on the way back in — the data may have changed while
      // the session was dead.
      await queryClient.invalidateQueries();

      // 112-offline-drafts.md §9. The queue a 401 paused drains NOW, in order, with fresh
      // cookies and a fresh CSRF token — and the work the user did while signed out
      // lands. Some entries may then 412 (the session was long enough for something to
      // change); those go to the recovery banner. Still not dropped.
      outbox.resume();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Explicit logout is the ONE case where the queue is drained FIRST (`111` §8.2).
      // If the drain fails the user is warned — and if they insist, the queue still
      // REMAINS. It replays when they sign back in. Signing out is not a way to throw
      // work away by accident.
      try {
        await outbox.drainToCompletion();
      } catch {
        const anyway = window.confirm(
          'You have unsynced changes. They are saved on this device and will sync next time you sign in.\n\nSign out anyway?',
        );
        if (!anyway) throw new Error('cancelled');
      }
      await logout();
    },
    onSuccess: () => {
      queryClient.setQueryData(keys.session(), null);
      // The SERVER-STATE cache only. IndexedDB (drafts, outbox, pendingUploads) is never
      // touched by any auth path, here or anywhere else.
      queryClient.clear();
    },
  });
}
